import { useEffect, useRef, useState } from "react";
import {
  ArrowRight, Car, Bus, CheckCircle2,
  ClipboardList, Copy, LogOut, MapPin, MessageCircle,
  Save, Trash2, Edit3, Plus, Lock,
} from "lucide-react";
import "./Admin.css";
import MapplsMap from "./MapplsMap";

const ADMIN_PASSWORD = "cabnaina2026";
const MAPPLS_KEY = "vfccvdtdtrwglhnqaudxmysbkzyldratuaid";
const MAPPLS_TEXTSEARCH = "https://search.mappls.com/search/places/textsearch/json";
const WA = "https://wa.me/918124290402";

const DEFAULT_TARIFF = {
  sedan: {
    label: "Sedan", icon: "Car", seats: "4",
    oneWay:    { perKm: 15, batta: 400,  minKm: 130 },
    roundTrip: { perKm: 12, batta: 500,  minKmDay: 250 },
    localPerHour: 350, localKmPerHr: 10, localExtraKm: 25,
    airportPerKm: 15, airportBatta: 400, airportMinKm: 30,
  },
  suv: {
    label: "SUV", icon: "Car", seats: "6",
    oneWay:    { perKm: 19, batta: 500,  minKm: 130 },
    roundTrip: { perKm: 18, batta: 600,  minKmDay: 250 },
    localPerHour: 450, localKmPerHr: 10, localExtraKm: 35,
    airportPerKm: 19, airportBatta: 500, airportMinKm: 30,
  },
  tempo12: {
    label: "Tempo 12-Seat", icon: "Bus", seats: "12",
    oneWay:    { perKm: 24, batta: 1000, minKm: 300 },
    roundTrip: { perKm: 24, batta: 1000, minKmDay: 300 },
    local5Hr: 3000, local10Hr: 5500,
    airportPerKm: 24, airportBatta: 1000, airportMinKm: 50,
  },
  coach21: {
    label: "Coach 21-Seat (AC)", icon: "Bus", seats: "21",
    oneWay:    { perKm: 30, batta: 1200, minKm: 300 },
    roundTrip: { perKm: 30, batta: 1200, minKmDay: 300 },
    local5Hr: 5000, local10Hr: 9500,
    airportPerKm: 30, airportBatta: 1200, airportMinKm: 50,
  },
  coach25: {
    label: "Coach 25-Seat (AC)", icon: "Bus", seats: "25",
    oneWay:    { perKm: 33, batta: 1200, minKm: 300 },
    roundTrip: { perKm: 33, batta: 1200, minKmDay: 300 },
    local5Hr: 5500, local10Hr: 10500,
    airportPerKm: 33, airportBatta: 1200, airportMinKm: 50,
  },
};

function loadTariff() {
  try {
    const stored = localStorage.getItem("cabnaina_tariff");
    return stored ? JSON.parse(stored) : DEFAULT_TARIFF;
  } catch { return DEFAULT_TARIFF; }
}

function loadLeads() {
  try {
    const stored = localStorage.getItem("cabnaina_leads");
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

function saveTariff(data) { localStorage.setItem("cabnaina_tariff", JSON.stringify(data)); }
function saveLeads(data) { localStorage.setItem("cabnaina_leads", JSON.stringify(data)); }

async function mapplsSearch(q) {
  if (!q || q.length < 3) return [];
  try {
    const r = await fetch(
      `${MAPPLS_TEXTSEARCH}?query=${encodeURIComponent(q)}&region=ind&access_token=${MAPPLS_KEY}`
    );
    if (!r.ok) throw new Error("Mappls textsearch failed");
    const d = await r.json();
    const places = [...(d.suggestedLocations || []), ...(d.userAddedLocations || [])];
    return places.map((p) => ({
      label: [p.placeName, p.placeAddress].filter(Boolean).join(", "),
      eLoc: p.eLoc,
    }));
  } catch {
    return [];
  }
}

function LocationInput({ label, value, onSelect, placeholder }) {
  const [text, setText] = useState(value?.label || "");
  const [sugg, setSugg] = useState([]);
  const timer = useRef(null);

  useEffect(() => { setText(value?.label || ""); }, [value]);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    onSelect(null);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const res = await mapplsSearch(v);
      setSugg(res);
    }, 380);
  };

  const pick = (place) => { setText(place.label); setSugg([]); onSelect(place); };

  return (
    <div className="adm-location-wrap">
      <label className="adm-label">{label}</label>
      <div className="adm-autocomplete">
        <MapPin size={14} className="adm-pin-icon" />
        <input
          type="text"
          value={text}
          onChange={onChange}
          placeholder={placeholder || "Type a city or place..."}
          className="adm-input adm-input-loc"
          autoComplete="off"
        />
        {sugg.length > 0 && (
          <ul className="adm-sugg-list">
            {sugg.map((s, i) => (
              <li key={i} onClick={() => pick(s)}>
                <MapPin size={12} /> {s.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function NumInput({ label, value, onChange, prefix = "₹", suffix }) {
  return (
    <div className="adm-field">
      <label className="adm-label">{label}</label>
      <div className="adm-num-wrap">
        {prefix && <span className="adm-affix">{prefix}</span>}
        <input type="number" className="adm-input adm-num" value={value} min={0}
          onChange={(e) => onChange(Number(e.target.value))} />
        {suffix && <span className="adm-affix adm-affix-r">{suffix}</span>}
      </div>
    </div>
  );
}

/* ── TARIFF EDITOR ── */
function TariffEditor() {
  const [tariff, setTariff] = useState(loadTariff);
  const [saved, setSaved] = useState(false);

  const update = (vehicleId, path, value) => {
    setTariff((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next[vehicleId];
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setSaved(false);
  };

  const handleSave = () => {
    saveTariff(tariff);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    if (window.confirm("Reset all tariff to defaults?")) {
      setTariff(DEFAULT_TARIFF);
      saveTariff(DEFAULT_TARIFF);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  return (
    <div className="adm-tariff">
      <div className="adm-section-head">
        <h2>Tariff Editor</h2>
        <div className="adm-section-actions">
          <button className="adm-btn adm-btn-ghost" onClick={handleReset}>Reset defaults</button>
          <button className="adm-btn adm-btn-primary" onClick={handleSave}>
            {saved ? <><CheckCircle2 size={15} /> Saved!</> : <><Save size={15} /> Save Tariff</>}
          </button>
        </div>
      </div>
      <p className="adm-hint">Changes are saved to browser storage and reflected in the trip estimator immediately.</p>

      {Object.entries(tariff).map(([id, v]) => {
        const hasHourly = v.localPerHour !== undefined;
        const hasPackage = v.local5Hr !== undefined;
        return (
          <div key={id} className="adm-vehicle-block">
            <div className="adm-vehicle-head">
              {v.icon === "Bus" ? <Bus size={18} /> : <Car size={18} />}
              <h3>{v.label}</h3>
              <span className="adm-badge">{v.seats} seats</span>
            </div>

            <div className="adm-rate-section">
              <h4>One-Way Outstation</h4>
              <div className="adm-rate-row">
                <NumInput label="Per km" value={v.oneWay.perKm} suffix="/km"
                  onChange={(val) => update(id, "oneWay.perKm", val)} />
                <NumInput label="Driver Batta" value={v.oneWay.batta}
                  onChange={(val) => update(id, "oneWay.batta", val)} />
                <NumInput label="Min. km" value={v.oneWay.minKm} prefix="" suffix="km"
                  onChange={(val) => update(id, "oneWay.minKm", val)} />
              </div>
            </div>

            <div className="adm-rate-section">
              <h4>Round-Trip Outstation</h4>
              <div className="adm-rate-row">
                <NumInput label="Per km" value={v.roundTrip.perKm} suffix="/km"
                  onChange={(val) => update(id, "roundTrip.perKm", val)} />
                <NumInput label="Driver Batta" value={v.roundTrip.batta}
                  onChange={(val) => update(id, "roundTrip.batta", val)} />
                <NumInput label="Min. km/day" value={v.roundTrip.minKmDay} prefix="" suffix="km"
                  onChange={(val) => update(id, "roundTrip.minKmDay", val)} />
              </div>
            </div>

            {hasHourly && (
              <div className="adm-rate-section">
                <h4>Local / Hourly</h4>
                <div className="adm-rate-row">
                  <NumInput label="Per hour" value={v.localPerHour} suffix="/hr"
                    onChange={(val) => update(id, "localPerHour", val)} />
                  <NumInput label="km included/hr" value={v.localKmPerHr} prefix="" suffix="km"
                    onChange={(val) => update(id, "localKmPerHr", val)} />
                  <NumInput label="Extra km charge" value={v.localExtraKm} suffix="/km"
                    onChange={(val) => update(id, "localExtraKm", val)} />
                </div>
              </div>
            )}

            {hasPackage && (
              <div className="adm-rate-section">
                <h4>Local Package</h4>
                <div className="adm-rate-row">
                  <NumInput label="5-hour package" value={v.local5Hr}
                    onChange={(val) => update(id, "local5Hr", val)} />
                  <NumInput label="10-hour package" value={v.local10Hr}
                    onChange={(val) => update(id, "local10Hr", val)} />
                </div>
              </div>
            )}

            <div className="adm-rate-section">
              <h4>Airport Transfer</h4>
              <div className="adm-rate-row">
                <NumInput label="Per km" value={v.airportPerKm} suffix="/km"
                  onChange={(val) => update(id, "airportPerKm", val)} />
                <NumInput label="Driver Batta" value={v.airportBatta}
                  onChange={(val) => update(id, "airportBatta", val)} />
                <NumInput label="Min. km" value={v.airportMinKm} prefix="" suffix="km"
                  onChange={(val) => update(id, "airportMinKm", val)} />
              </div>
            </div>
          </div>
        );
      })}

      <div className="adm-save-bar">
        <button className="adm-btn adm-btn-primary adm-btn-lg" onClick={handleSave}>
          {saved ? <><CheckCircle2 size={16} /> Tariff Saved!</> : <><Save size={16} /> Save All Changes</>}
        </button>
      </div>
    </div>
  );
}

/* ── LEAD POST EDITOR ── */
const EMPTY_LEAD = {
  company: "CAB NAINA TOURS & TRAVELS",
  from: null, to: null,
  tripType: "One way drop",
  date: "", time: "",
  vehicle: "Any Suv",
  rateKm: 19, rateBatta: 400,
  commission: 10,
  website: "www.cabnaina.com",
  phone: "8124290402",
  notes: "",
};

const TRIP_OPTIONS = ["One way drop", "Round trip", "Local rental", "Airport pickup", "Airport drop"];
const VEHICLE_OPTIONS = ["Any Sedan", "Any Suv", "Tempo 12 Seat", "Tempo 14 Seat", "Coach 21 Seat", "Coach 25 Seat"];

function buildLeadText(l) {
  const from = l.from?.label || "-";
  const to = l.to?.label || "-";
  return [
    l.company,
    "",
    `${from} to ${to}`,
    "",
    l.tripType,
    l.date,
    l.time,
    l.vehicle,
    `*${l.rateKm}/${l.rateBatta}`,
    "",
    `${l.commission}%CC & gst`,
    "",
    l.website,
    l.phone,
    l.notes ? l.notes : null,
  ].filter((x) => x !== null).join("\n");
}

function LeadForm({ initial, onSave, onCancel }) {
  const [lead, setLead] = useState(initial || EMPTY_LEAD);
  const set = (k, v) => setLead((p) => ({ ...p, [k]: v }));
  const text = buildLeadText(lead);

  const copy = () => navigator.clipboard.writeText(text).catch(() => {});

  const waLink = `${WA}?text=${encodeURIComponent(text)}`;

  return (
    <div className="adm-lead-form">
      <div className="adm-lead-form-grid">

        <div className="adm-field">
          <label className="adm-label">Company Name</label>
          <input className="adm-input" value={lead.company}
            onChange={(e) => set("company", e.target.value)} />
        </div>

        <LocationInput label="From (Pickup)" value={lead.from}
          onSelect={(p) => set("from", p)} placeholder="E.g. Chennai Airport" />

        <LocationInput label="To (Drop)" value={lead.to}
          onSelect={(p) => set("to", p)} placeholder="E.g. Serkali" />

        <div className="adm-field">
          <label className="adm-label">Trip Type</label>
          <select className="adm-input" value={lead.tripType} onChange={(e) => set("tripType", e.target.value)}>
            {TRIP_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>

        <div className="adm-field">
          <label className="adm-label">Date</label>
          <input type="date" className="adm-input" value={lead.date}
            onChange={(e) => set("date", e.target.value.split("-").reverse().join("/"))} />
        </div>

        <div className="adm-field">
          <label className="adm-label">Time</label>
          <input type="time" className="adm-input" value={lead.time}
            onChange={(e) => {
              const [h, m] = e.target.value.split(":");
              const hr = +h;
              const suffix = hr >= 12 ? "Pm" : "Am";
              const h12 = hr % 12 || 12;
              set("time", `${h12}.${m} ${suffix}`);
            }} />
        </div>

        <div className="adm-field">
          <label className="adm-label">Vehicle</label>
          <select className="adm-input" value={lead.vehicle} onChange={(e) => set("vehicle", e.target.value)}>
            {VEHICLE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>

        <NumInput label="Rate / km" value={lead.rateKm} suffix="/km"
          onChange={(v) => set("rateKm", v)} />
        <NumInput label="Driver Batta" value={lead.rateBatta}
          onChange={(v) => set("rateBatta", v)} />
        <NumInput label="Commission %" value={lead.commission} prefix="" suffix="%"
          onChange={(v) => set("commission", v)} />

        <div className="adm-field">
          <label className="adm-label">Website</label>
          <input className="adm-input" value={lead.website}
            onChange={(e) => set("website", e.target.value)} />
        </div>

        <div className="adm-field">
          <label className="adm-label">Phone</label>
          <input className="adm-input" type="tel" value={lead.phone}
            onChange={(e) => set("phone", e.target.value)} />
        </div>

        <div className="adm-field adm-field-full">
          <label className="adm-label">Notes (optional)</label>
          <textarea className="adm-input adm-textarea" rows={2} value={lead.notes}
            onChange={(e) => set("notes", e.target.value)} />
        </div>
      </div>

      {/* Preview */}
      <div className="adm-lead-preview">
        <div className="adm-lead-preview-head">
          <span><ClipboardList size={15} /> Lead Preview</span>
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={copy}>
            <Copy size={13} /> Copy text
          </button>
        </div>
        <pre className="adm-lead-pre">{text}</pre>
        <div className="adm-lead-preview-actions">
          <a href={waLink} target="_blank" rel="noreferrer" className="adm-btn adm-btn-wa">
            <MessageCircle size={15} /> Send on WhatsApp
          </a>
          <button className="adm-btn adm-btn-primary" onClick={() => onSave(lead)}>
            <Save size={15} /> Save Lead
          </button>
          {onCancel && (
            <button className="adm-btn adm-btn-ghost" onClick={onCancel}>Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
}

function LeadPosts() {
  const [leads, setLeads] = useState(loadLeads);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const saveLead = (lead) => {
    let next;
    if (editingId !== null) {
      next = leads.map((l, i) => (i === editingId ? { ...lead, savedAt: new Date().toISOString() } : l));
    } else {
      next = [{ ...lead, savedAt: new Date().toISOString() }, ...leads];
    }
    setLeads(next);
    saveLeads(next);
    setCreating(false);
    setEditingId(null);
  };

  const deleteLead = (i) => {
    if (!window.confirm("Delete this lead?")) return;
    const next = leads.filter((_, idx) => idx !== i);
    setLeads(next);
    saveLeads(next);
  };

  return (
    <div className="adm-leads">
      <div className="adm-section-head">
        <h2>Lead Posts</h2>
        {!creating && editingId === null && (
          <button className="adm-btn adm-btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} /> New Lead
          </button>
        )}
      </div>
      <p className="adm-hint">Create and manage cab lead posts. Copy the formatted text or send directly to WhatsApp.</p>

      {(creating || editingId !== null) && (
        <div className="adm-form-card">
          <h3>{editingId !== null ? "Edit Lead" : "New Lead Post"}</h3>
          <LeadForm
            initial={editingId !== null ? leads[editingId] : null}
            onSave={saveLead}
            onCancel={() => { setCreating(false); setEditingId(null); }}
          />
        </div>
      )}

      {!creating && editingId === null && leads.length === 0 && (
        <div className="adm-empty">
          <ClipboardList size={40} />
          <p>No leads saved yet. Click "New Lead" to create one.</p>
        </div>
      )}

      <div className="adm-leads-list">
        {leads.map((lead, i) => {
          if (editingId === i) return null;
          const text = buildLeadText(lead);
          const waLink = `${WA}?text=${encodeURIComponent(text)}`;
          const from = lead.from?.label || "-";
          const to = lead.to?.label || "-";
          const savedDate = lead.savedAt ? new Date(lead.savedAt).toLocaleDateString("en-IN") : "";
          return (
            <div key={i} className="adm-lead-card">
              <div className="adm-lead-card-head">
                <div>
                  <span className="adm-lead-route">{from} → {to}</span>
                  <span className="adm-lead-meta">{lead.tripType} · {lead.vehicle} · {lead.date} {lead.time}</span>
                  {savedDate && <span className="adm-lead-saved">Saved {savedDate}</span>}
                </div>
                <div className="adm-lead-card-actions">
                  <button className="adm-icon-btn" title="Edit" onClick={() => setEditingId(i)}><Edit3 size={15} /></button>
                  <button className="adm-icon-btn" title="Copy" onClick={() => navigator.clipboard.writeText(text).catch(() => {})}><Copy size={15} /></button>
                  <a href={waLink} target="_blank" rel="noreferrer" className="adm-icon-btn adm-icon-wa" title="WhatsApp"><MessageCircle size={15} /></a>
                  <button className="adm-icon-btn adm-icon-del" title="Delete" onClick={() => deleteLead(i)}><Trash2 size={15} /></button>
                </div>
              </div>
              <pre className="adm-lead-preview-mini">{text}</pre>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── LOGIN GATE ── */
function LoginGate({ onLogin }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (pw === ADMIN_PASSWORD) { onLogin(); }
    else { setErr(true); setTimeout(() => setErr(false), 1800); }
  };
  return (
    <div className="adm-login">
      <div className="adm-login-card">
        <span className="adm-login-icon"><Lock size={28} /></span>
        <h1>Admin Panel</h1>
        <p>Enter the admin password to continue.</p>
        <form onSubmit={submit} className="adm-login-form">
          <input
            type="password"
            placeholder="Password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className={`adm-input adm-login-input ${err ? "adm-input-err" : ""}`}
            autoFocus
          />
          {err && <p className="adm-err-msg">Incorrect password</p>}
          <button type="submit" className="adm-btn adm-btn-primary adm-btn-lg">
            <ArrowRight size={16} /> Enter
          </button>
        </form>
        <a href="/" className="adm-back-link">← Back to site</a>
      </div>
    </div>
  );
}

/* ── MAIN ADMIN ── */
export default function Admin() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("adm_auth") === "1");
  const [tab, setTab] = useState("tariff");

  const login = () => { sessionStorage.setItem("adm_auth", "1"); setAuthed(true); };
  const logout = () => { sessionStorage.removeItem("adm_auth"); setAuthed(false); };

  if (!authed) return <LoginGate onLogin={login} />;

  return (
    <div className="adm-app">
      <nav className="adm-nav">
        <a href="/" className="adm-nav-logo">
          <img src="/favicon.svg" alt="" width="24" height="24" style={{ borderRadius: 6 }} />
          cab<b>naina</b> <span>Admin</span>
        </a>
        <div className="adm-nav-tabs">
          <button className={tab === "tariff" ? "is-active" : ""} onClick={() => setTab("tariff")}>
            <ClipboardList size={16} /> Tariff
          </button>
          <button className={tab === "leads" ? "is-active" : ""} onClick={() => setTab("leads")}>
            <Megaphone size={16} /> Lead Posts
          </button>
          <button className={tab === "map" ? "is-active" : ""} onClick={() => setTab("map")}>
            <MapPin size={16} /> Map
          </button>
        </div>
        <button className="adm-logout" onClick={logout}><LogOut size={16} /> Logout</button>
      </nav>

      <main className="adm-main">
        {tab === "tariff" && <TariffEditor />}
        {tab === "leads" && <LeadPosts />}
        {tab === "map" && <MapplsMap />}
      </main>
    </div>
  );
}

function Megaphone(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width={props.size || 24} height={props.size || 24}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 11 18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}
